/**
 * Utilitários para inputs numéricos
 * Permite entrada de valores decimais com ponto ou vírgula
 */

/**
 * Processa valor de input numérico
 * - Converte vírgula para ponto
 * - Remove caracteres inválidos
 * - Garante apenas um ponto decimal
 * @param {string} value - Valor do input
 * @returns {string} - Valor processado
 */
export const processNumericInput = (value) => {
  if (typeof value !== 'string') return value;
  
  // Substituir vírgula por ponto
  let processed = value.replace(/,/g, '.');
  
  // Remover caracteres inválidos (manter apenas números e ponto)
  processed = processed.replace(/[^0-9.]/g, '');
  
  // Garantir que só há um ponto decimal
  const parts = processed.split('.');
  if (parts.length > 2) {
    processed = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Remover zeros à esquerda desnecessários (mas manter "0" e "0.x")
  if (processed.match(/^0+[1-9]/)) {
    processed = processed.replace(/^0+/, '');
  }
  
  return processed;
};

/**
 * Handler para onChange de inputs numéricos
 * @param {Function} setter - Função para definir o valor (setState ou similar)
 * @param {Event} e - Evento do input
 */
export const handleNumericChange = (setter) => (e) => {
  const processed = processNumericInput(e.target.value);
  setter(processed);
};

/**
 * Handler para o evento onKeyDown - SIMPLIFICADO
 * Apenas permite números, ponto, vírgula e teclas de controlo
 * @param {KeyboardEvent} e - Evento de teclado
 */
export const handleNumericKeyDown = (e) => {
  // Permitir: backspace, delete, tab, escape, enter, setas
  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
  if (allowedKeys.includes(e.key)) {
    return;
  }
  
  // Permitir Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
  if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
    return;
  }
  
  // Permitir números
  if (e.key >= '0' && e.key <= '9') {
    return;
  }
  
  // Permitir ponto e vírgula (serão convertidos no onChange)
  if (e.key === '.' || e.key === ',' || e.key === 'Decimal') {
    return;
  }
  
  // Bloquear qualquer outra tecla
  e.preventDefault();
};

/**
 * Handler para onChange de inputs numéricos com callback
 * @param {Event} e - Evento do input
 * @param {Function} callback - Função para definir o valor processado
 */
export const handleNumericInput = (e, callback) => {
  const processed = processNumericInput(e.target.value);
  if (callback && typeof callback === 'function') {
    callback(processed);
  }
  return processed;
};

/**
 * Formata número para exibição no formato português (vírgula como separador decimal)
 * @param {number} value - Valor numérico
 * @param {number} decimals - Casas decimais (default: 2)
 * @returns {string} - Valor formatado
 */
export const formatarNumeroPortugues = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '0,00';
  return Number(value).toFixed(decimals).replace('.', ',');
};

/**
 * Converte string no formato português para número
 * @param {string} value - Valor em string (pode ter vírgula ou ponto)
 * @returns {number} - Valor numérico
 */
export const parseNumeroPortugues = (value) => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  return parseFloat(String(value).replace(',', '.')) || 0;
};

/**
 * Props padrão para inputs numéricos
 * Usar spread: {...numericInputProps}
 */
export const numericInputProps = {
  type: 'text',
  inputMode: 'decimal',
  autoComplete: 'off'
};

export default {
  processNumericInput,
  handleNumericChange,
  handleNumericKeyDown,
  handleNumericInput,
  numericInputProps,
  formatarNumeroPortugues,
  parseNumeroPortugues
};
